import { useState, useEffect, useMemo, useCallback } from "react";
import Mixpanel from "mixpanel-browser";
import { parseUnits, formatUnits, maxInt256 } from "viem";
import {
  useConnect,
  useAccount,
  useDisconnect,
  useReadContract,
  useWriteContract,
  useWaitForTransactionReceipt,
  useBalance,
} from "wagmi";
import {
  Wallet,
  Loader2,
  CreditCard,
  Lock,
  ToggleLeft,
  ToggleRight,
  Newspaper,
  AlertCircle,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { config } from "./config";
import LendingManagerABI from "./contracts/LendingManagerBaseV1.sol/LendingManagerBaseV1.json";
import FeesABI from "./contracts/Fees.sol/Fees.json";
import ERC20ABI from "./contracts/ERC20/IERC20.sol/IERC20.json";
import MarketNewsData from "./data/market-news.json";

// TODO: Move out of file
const parseFormattedNumber = (formattedString: string | any) => {
  if (typeof formattedString !== "string") {
    throw new Error("Input must be a string");
  }

  // Remove commas and trim spaces
  const cleanedString = formattedString.replace(/,/g, "").trim();

  // Check if cleaned string is a valid number
  const parsedNumber = parseFloat(cleanedString);

  if (isNaN(parsedNumber)) {
    throw new Error("Invalid number format");
  }

  return parsedNumber;
};

// TODO: Move out of file
// News Component
const MarketNews = () => {
  const [news, setNews] = useState<any>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        setNews(MarketNewsData.news);
        setLoading(false);
      } catch (err) {
        setError("Failed to load market news");
        setLoading(false);
      }
    };
    fetchNews();
  }, []);

  if (loading)
    return (
      <div className="animate-pulse p-4 rounded-xl bg-[#2B2F3B] space-y-4">
        <div className="h-4 bg-[#363A41] rounded w-3/4"></div>
        <div className="h-4 bg-[#363A41] rounded w-1/2"></div>
      </div>
    );

  if (error)
    return (
      <div className="p-4 rounded-xl bg-red-900/20 border border-red-900/50 text-red-500">
        <div className="flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          <span className="font-medium">Error</span>
        </div>
        <p className="mt-2 ml-7 text-sm">{error}</p>
      </div>
    );

  return (
    <div className="p-4 mb-6 rounded-xl bg-[#2B2F3B]">
      <div className="flex items-center gap-2 mb-4">
        <Newspaper className="h-5 w-5 text-[#00A69C]" />
        <h3 className="text-lg font-medium text-white">Weekly Market News</h3>
      </div>
      <div className="space-y-4">
        {news.map((item: any, index: number) => (
          <a key={index} href={item.link} target="_blank" rel="noreferrer">
            <div key={index} className="p-3 mb-4 rounded-lg bg-[#1B2030]">
              <h4 className="text-sm font-medium text-white mb-1">
                {item.title}
              </h4>
              <p className="text-sm text-gray-400">{item.summary}</p>
              <div className="flex justify-between items-center mt-2">
                <span className="text-xs text-gray-500">{item.source}</span>
                <span className="text-xs text-gray-500">{item.date}</span>
              </div>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
};

const LendingInterface = () => {
  const [selectedToken, setSelectedToken] = useState("USDC");
  const [amount, setAmount] = useState("0");
  const [userBalance, setUserBalance] = useState("0");
  const [totalDeposited, setTotalDeposited] = useState("0");
  const [apy, setApy] = useState("0");
  const [isDeposit, setIsDeposit] = useState(true);

  const { connect, connectors } = useConnect();
  const { address, isConnected } = useAccount();
  const { disconnect } = useDisconnect();

  const contract =
    selectedToken === "USDC"
      ? config.contracts.USDCLendingManager
      : config.contracts.USDTLendingManager;

  // Contract reads
  const { data: tokenBalance, refetch: refetchTokenBalance } = useBalance({
    address: address,
    token: config.contracts[selectedToken].address,
  });

  const { data: userBalanceData, refetch: refetchUserBalance } =
    useReadContract({
      address: contract.address,
      abi: LendingManagerABI.abi,
      functionName: "getUserBalance",
      args: [address],
    });

  const { data: totalDepositedData, refetch: refetchDepositAmount } =
    useReadContract({
      address: contract.address,
      abi: LendingManagerABI.abi,
      functionName: "totalDeposited",
    });

  const { data: apyData, refetch: refetchAPYAmount } = useReadContract({
    address: contract.address,
    abi: LendingManagerABI.abi,
    functionName: "getAPYFromProtocolReserve",
  });

  const { data: allowanceData, refetch: refetchAllowanceAmount } =
    useReadContract({
      address: config.contracts[selectedToken].address,
      abi: ERC20ABI.abi,
      functionName: "allowance",
      args: [address ?? "0x", contract.address],
    });

  const { data: serviceFeeData, refetch: refetchServiceFee } = useReadContract({
    address: config.contracts.Fees.address,
    abi: FeesABI.abi,
    functionName: "getServiceFee",
  });

  const {
    data: totalFeesGeneratedData,
    refetch: refetchTotalFeesGenerated,
  }: any = useReadContract({
    address: config.contracts.Fees.address,
    abi: FeesABI.abi,
    functionName: "getTotalFeesGenerated",
  });

  const { data: getCurrentValueData, refetch: refetchGetCurrentValue }: any =
    useReadContract({
      address: contract.address,
      abi: LendingManagerABI.abi,
      functionName: "getCurrentValue",
      args: [address],
    });

  const { data: yieldGeneratedData, refetch: refetchYieldGenerated }: any =
    useReadContract({
      address: contract.address,
      abi: LendingManagerABI.abi,
      functionName: "yieldGenerated",
    });

  const { data: usdcTotalDepositData }: any = useReadContract({
    address: config.contracts.USDCLendingManager.address,
    abi: LendingManagerABI.abi,
    functionName: "totalDeposited",
  });

  const { data: usdtTotalDepositData }: any = useReadContract({
    address: config.contracts.USDTLendingManager.address,
    abi: LendingManagerABI.abi,
    functionName: "totalDeposited",
  });

  const {
    data: usdcTotalSuppliedData,
    refetch: refetchUSDCTotalSupplied,
  }: any = useReadContract({
    address: config.contracts.USDCLendingManager.address,
    abi: LendingManagerABI.abi,
    functionName: "getTotalSuppliedToProtocol",
  });

  const {
    data: usdtTotalSuppliedData,
    refetch: refetchUSDTTotalSupplied,
  }: any = useReadContract({
    address: config.contracts.USDTLendingManager.address,
    abi: LendingManagerABI.abi,
    functionName: "getTotalSuppliedToProtocol",
  });

  // Contract writes
  const { writeContract: deposit, data: depositDataHash } = useWriteContract();

  const { writeContract: withdraw, data: withdrawDataHash } =
    useWriteContract();

  const { writeContract: approveToken, data: approveTokenDataHash } =
    useWriteContract();

  // Transaction states
  const { isLoading: isDepositing, isSuccess: depositSuccess } =
    useWaitForTransactionReceipt({
      hash: depositDataHash,
    });

  const { isLoading: isWithdrawing, isSuccess: withdrawSuccess } =
    useWaitForTransactionReceipt({
      hash: withdrawDataHash,
    });

  const { isLoading: isApproving, isSuccess: approveSuccess } =
    useWaitForTransactionReceipt({
      hash: approveTokenDataHash,
    });

  // Memoized calculations
  const serviceFee = useMemo(() => {
    if (!serviceFeeData) return 0;
    return Number(serviceFeeData);
  }, [serviceFeeData]);

  const calculateFeeAmount = useMemo(() => {
    if (!amount || !serviceFee) return "0";
    const parsedAmount = parseFloat(amount);
    return ((parsedAmount * serviceFee) / 100).toString();
  }, [amount, serviceFee]);

  const finalAmount = useMemo(() => {
    if (!amount || !calculateFeeAmount) return "0";
    const parsedAmount = parseFloat(amount);
    const parsedFee = parseFloat(calculateFeeAmount);
    return (parsedAmount - parsedFee).toString();
  }, [amount, calculateFeeAmount]);

  const needsApproval = useMemo(() => {
    if (!amount || typeof allowanceData !== "bigint" || !isDeposit) {
      return false;
    }
    if (typeof allowanceData === "bigint" && allowanceData === BigInt(0)) {
      return true;
    }
    try {
      const parsedAmount = parseUnits(
        amount,
        config.contracts[selectedToken].decimals
      );
      return parsedAmount > BigInt(allowanceData as any);
    } catch {
      return false;
    }
  }, [amount, allowanceData, isDeposit, selectedToken]);

  const formattedValues = useMemo(() => {
    const formatValue = (value: bigint | undefined) =>
      value
        ? Number(formatUnits(value, contract.decimals)).toLocaleString(
            undefined,
            {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }
          )
        : "0.00";

    return {
      initialDeposit: getCurrentValueData?.[0]
        ? formatValue(getCurrentValueData[0])
        : "0.00",
      currentValue: getCurrentValueData?.[1]
        ? formatValue(getCurrentValueData[1])
        : "0.00",
      estimatedYield: getCurrentValueData?.[2]
        ? formatValue(getCurrentValueData[2])
        : "0.00",
      totalFeesGenerated: totalFeesGeneratedData
        ? formatValue(totalFeesGeneratedData)
        : "0.00",
      yieldGenerated: yieldGeneratedData
        ? formatValue(yieldGeneratedData)
        : "0.00",
    };
  }, [
    getCurrentValueData,
    totalFeesGeneratedData,
    yieldGeneratedData,
    contract.decimals,
  ]);

  // Calculate total real-time yield using the difference between total supplied and total deposited
  const calculatedTotalYield = useMemo(() => {
    const calculateYield = (
      totalSupplied: bigint | undefined,
      totalDeposited: bigint | undefined
    ) => {
      if (!totalSupplied || !totalDeposited) return BigInt(0);
      return totalSupplied > totalDeposited
        ? totalSupplied - totalDeposited
        : BigInt(0);
    };

    const usdcYield = calculateYield(
      usdcTotalSuppliedData,
      usdcTotalDepositData
    );
    const usdtYield = calculateYield(
      usdtTotalSuppliedData,
      usdtTotalDepositData
    );

    // Convert to proper decimal format based on selected token
    const formattedYield = formatUnits(
      selectedToken === "USDC" ? usdcYield : usdtYield,
      contract.decimals
    );

    return Number(formattedYield).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }, [
    usdcTotalSuppliedData,
    usdtTotalSuppliedData,
    usdcTotalDepositData,
    usdtTotalDepositData,
    selectedToken,
    contract.decimals,
  ]);

  const handleAction = async () => {
    if (!amount || !isConnected) return;

    const parsedAmount = parseUnits(amount, contract.decimals);

    if (isDeposit) {
      // Check if approval is needed
      if (needsApproval) {
        // Approve max uint256 to save gas on future transactions
        approveToken({
          address: config.contracts[selectedToken].address,
          abi: ERC20ABI.abi,
          functionName: "approve",
          args: [contract.address, maxInt256],
        });
        return;
      }

      deposit({
        address: contract.address,
        abi: LendingManagerABI.abi,
        functionName: "deposit",
        args: [parsedAmount],
      });
    } else {
      withdraw({
        address: contract.address,
        abi: LendingManagerABI.abi,
        functionName: "withdraw",
        args: [parsedAmount],
      });
    }
  };

  // Update button text based on action state
  const getButtonText = () => {
    if (!isConnected) return "Connect Wallet";
    if (isApproving) return "Approving...";
    if (isDepositing) return "Depositing...";
    if (isWithdrawing) return "Withdrawing...";
    if (
      isDeposit &&
      (typeof allowanceData !== "bigint" || allowanceData === BigInt(0))
    ) {
      return "Approve";
    }
    return isDeposit ? "Deposit" : "Withdraw";
  };

  // TODO: Move out of file
  const EarningsProjection = ({
    amount,
    currentTotalDeposited,
    apy,
    selectedToken,
  }: any) => {
    const [isWeeklyCompound, setIsWeeklyCompound] = useState(false);

    const projectedEarnings = useMemo(() => {
      if (!amount || !apy) return [];

      const periods = [
        { months: 12, label: "1Y" },
        { months: 24, label: "2Y" },
        { months: 48, label: "4Y" },
      ];

      const weeklyRate = Number(apy) / 52 / 100;
      const monthlyRate = Number(apy) / 12 / 100;
      const newAmount = Number(amount) || 0;
      let totalDeposited = parseFormattedNumber(currentTotalDeposited) || 0;
      const existingAmount = totalDeposited;
      const totalPrincipal = newAmount + existingAmount;

      return periods.map(({ months, label }) => {
        let total, profit;

        if (isWeeklyCompound) {
          // Calculate weekly compounds over the years
          const weeks = months * 4.33; // Approximate weeks per month
          let balance = totalPrincipal;
          let totalDeposits = totalPrincipal;

          // Add new amount weekly and compound
          for (let week = 0; week < weeks; week++) {
            balance = (balance + newAmount) * (1 + weeklyRate);
            totalDeposits += newAmount;
          }

          total = balance;
          totalDeposited = totalDeposits;
          profit = total - totalDeposits; // Subtract all contributions
        } else {
          // Single deposit compound interest with both amounts: A = P(1 + r)^t
          total = totalPrincipal * Math.pow(1 + monthlyRate, months);
          totalDeposited = totalPrincipal;
          profit = total - totalPrincipal;
        }

        return {
          months,
          label,
          total,
          profit,
          totalDeposited,
        };
      });
    }, [amount, currentTotalDeposited, apy, isWeeklyCompound]);

    if (!amount || !apy || !projectedEarnings.length) return null;

    const formatValue = (value: any) =>
      Number(value).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

    const CustomTooltip = ({ active, payload, label }: any) => {
      if (!active || !payload || !payload.length) return null;

      const data = payload[0].payload;
      return (
        <div className="bg-[#1B2030] p-3 rounded-lg shadow-lg border border-[#363A41]">
          <p className="text-sm font-medium mb-1 text-white">
            {data.label} Projection
          </p>
          <div className="space-y-1 text-sm">
            <p className="text-gray-300">
              Total: {formatValue(data.total)} {selectedToken}
            </p>
            <p className="text-gray-300">
              Initial: {currentTotalDeposited} {selectedToken}
            </p>
            {Number(amount) > 0 && (
              <p className="text-gray-300">
                New: +{formatValue(Number(amount))} {selectedToken}
              </p>
            )}
            {isWeeklyCompound && (
              <p className="text-gray-300">
                Weekly Deposits: {formatValue(Number(amount))} {selectedToken}
              </p>
            )}
            <p className="text-teal-400 font-medium">
              Profit: +{formatValue(data.profit)} {selectedToken}
            </p>
          </div>
        </div>
      );
    };

    return (
      <div className="mb-6">
        <div className="p-4 rounded-xl border border-[#2B2F3B] bg-[#1B2030]">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-sm font-medium text-gray-300">
              Projected Earnings ({apy}% APY)
            </h3>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-300">Weekly Deposits</label>
              <button
                onClick={() => setIsWeeklyCompound(!isWeeklyCompound)}
                className="text-gray-300 hover:text-white transition-colors focus:outline-none"
              >
                {isWeeklyCompound ? (
                  <ToggleRight className="h-6 w-6 text-[#00A69C]" />
                ) : (
                  <ToggleLeft className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>

          {/* Chart */}
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={projectedEarnings.map((data) => ({
                  ...data,
                  selectedToken,
                  isWeeklyCompound,
                  deposited: isWeeklyCompound
                    ? Number(amount) * (data.months * 4.33)
                    : Number(amount),
                }))}
                margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
              >
                <XAxis
                  dataKey="label"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "#9CA3AF", fontSize: 12 }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "#9CA3AF", fontSize: 12 }}
                  tickFormatter={(value) =>
                    Number(value).toLocaleString(undefined, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })
                  }
                />
                <Tooltip content={<CustomTooltip />} />
                <Line
                  type="monotone"
                  dataKey="total"
                  stroke={selectedToken === "USDC" ? "#00A69C" : "#00796F"}
                  strokeWidth={2}
                  dot={{
                    fill: selectedToken === "USDC" ? "#00A69C" : "#00796F",
                    strokeWidth: 2,
                    r: 4,
                  }}
                  activeDot={{
                    fill: selectedToken === "USDC" ? "#00A69C" : "#00796F",
                    strokeWidth: 2,
                    r: 6,
                  }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Breakdown Cards */}
          <div className="grid grid-cols-3 gap-2 md:gap-4 mt-4">
            {projectedEarnings.map((projection) => (
              <div
                key={projection.label}
                className="p-2 md:p-3 rounded-lg bg-[#2B2F3B] text-center"
              >
                <p className="text-sm text-gray-400 mb-1 break-words">
                  {projection.label}
                </p>
                <p className="text-sm font-medium text-white break-words">
                  {formatValue(projection.total)} {selectedToken}
                </p>
                <p className="text-xs text-gray-400 break-words">
                  Initial: {currentTotalDeposited}
                </p>
                {Number(amount) > 0 && (
                  <p className="text-xs text-gray-400 break-words">
                    New: +{formatValue(Number(amount))}
                  </p>
                )}
                <p className="text-xs text-teal-400 mt-1 break-words">
                  +{formatValue(projection.profit)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    Mixpanel.init(process.env.REACT_APP_MIXPANEL_TOKEN as string, {
      debug: !config.inProd,
      ignore_dnt: !config.inProd,
    });
  }, []);

  // Update UI when contract data changes
  useEffect(() => {
    if (userBalanceData) {
      setUserBalance(formatUnits(userBalanceData as any, contract.decimals));
    }
    if (totalDepositedData) {
      setTotalDeposited(
        formatUnits(totalDepositedData as any, contract.decimals)
      );
    }
    if (apyData) {
      setApy(apyData.toString());
    }
  }, [
    userBalanceData,
    contract.decimals,
    totalDepositedData,
    apyData,
    selectedToken,
  ]);

  useEffect(() => {
    if (approveSuccess || depositSuccess || withdrawSuccess) {
      const refreshData = async () => {
        await Promise.all([
          refetchUserBalance(),
          refetchTokenBalance(),
          refetchDepositAmount(),
          refetchAPYAmount(),
          refetchServiceFee(),
          refetchAllowanceAmount(),
          refetchGetCurrentValue(),
          refetchTotalFeesGenerated(),
          refetchYieldGenerated(),
          refetchUSDCTotalSupplied(),
          refetchUSDTTotalSupplied(),
        ]);
      };
      refreshData();
    }
  }, [
    approveSuccess,
    depositSuccess,
    withdrawSuccess,
    refetchUserBalance,
    refetchTokenBalance,
    refetchDepositAmount,
    refetchAPYAmount,
    refetchServiceFee,
    refetchAllowanceAmount,
    refetchGetCurrentValue,
    refetchTotalFeesGenerated,
    refetchYieldGenerated,
    refetchUSDCTotalSupplied,
    refetchUSDTTotalSupplied,
  ]);

  const handleAmountInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setAmount(e.target.value);
    },
    []
  );

  return (
    <div className="min-h-screen bg-[#0D111C] p-4 md:p-6 lg:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-semibold text-white  font-black">
            EZLend ⚡️
          </h1>
          <div className="flex items-center gap-3">
            {/* Buy {selectedToken} Button */}
            <a
              href="https://app.uniswap.org/buy"
              target="_blank"
              rel="noreferrer"
              onClick={() => {
                Mixpanel.track(`Clicked Buy ${selectedToken} link`, {});
              }}
            >
              <button className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#2B2F3B] text-[#00A69C] hover:bg-[#363A41] transition-colors">
                <CreditCard className="h-4 w-4" />
                <span className="hidden md:inline">Buy {selectedToken}</span>
              </button>
            </a>

            {/* Wallet Connection */}
            {isConnected && address ? (
              <button
                onClick={() => disconnect()}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#2B2F3B] hover:bg-[#363A41] transition-colors text-gray-300"
              >
                <span className="hidden md:inline">{`${address.slice(
                  0,
                  6
                )}...${address.slice(-4)}`}</span>
                <Wallet className="h-5 w-5" />
              </button>
            ) : (
              <button
                onClick={() => connect({ connector: connectors[0] })}
                className="px-6 py-2 rounded-full bg-[#00796F] text-white hover:bg-[#00A69C] transition-colors"
              >
                Connect
              </button>
            )}
          </div>
        </div>

        {/* Introduction Section */}
        <div className="text-center mb-8 max-w-xl mx-auto">
          <div className="space-y-3 mb-4">
            <p className="text-gray-300 text-3xl font-black">
              Earn passive income while you sleep.
            </p>
            <p className="text-gray-400">
              No complex steps. Simply deposit or withdraw anytime.
            </p>
            <p className="text-gray-400">
              Powered by{" "}
              <a
                href="https://aave.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#00A69C] hover:text-[#00796F] underline decoration-dotted"
                onClick={() => {
                  Mixpanel.track(`Clicked 'Aave V3' link`, {});
                }}
              >
                Aave V3
              </a>
              's battle-tested protocol.
            </p>
          </div>
          <p className="text-sm text-gray-400 italic mt-4">
            *Currently live testing on{" "}
            <a
              className="text-[#00A69C] hover:text-[#00796F] underline decoration-dotted"
              href="https://support.metamask.io/networks-and-sidechains/eth-on-testnets/"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                Mixpanel.track(`Clicked 'Sepolia' link`, {});
              }}
            >
              Sepolia
            </a>
          </p>
        </div>

        {/* Total Locked Display */}
        <div className="flex items-center justify-center mb-6 p-4 rounded-xl bg-[#2B2F3B]">
          <div className="text-center">
            <div className="flex items-center gap-2 justify-center mb-1">
              <Lock className="h-4 w-4 text-gray-300" />
              <span className="text-sm text-gray-300">
                Total {selectedToken} Deposited
              </span>
            </div>
            <p className="text-2xl font-semibold text-white">
              {Number(totalDeposited).toLocaleString()} {selectedToken}
            </p>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="p-4 rounded-xl bg-[#2B2F3B]">
            <div className="text-center">
              <span className="text-sm text-gray-300">
                Protocol Fees Generated
              </span>
              <p className="text-xl font-semibold text-white">
                {formattedValues.totalFeesGenerated} {selectedToken}
              </p>
            </div>
          </div>
          <div className="p-4 rounded-xl bg-[#2B2F3B]">
            <div className="text-center">
              <span className="text-sm text-gray-300">
                Total Yield Generated
              </span>
              <p className="text-xl font-semibold text-white">
                Real-time: {calculatedTotalYield} {selectedToken}
              </p>
            </div>
          </div>
        </div>

        {/* Market News */}
        <MarketNews />

        {/* Main card */}
        <div className="bg-[#1B2030] rounded-3xl shadow-lg p-6 mb-6">
          {/* Token Selector */}
          <div className="flex gap-4 mb-6">
            <button
              onClick={() => setSelectedToken("USDC")}
              className={`flex-1 py-3 rounded-xl border transition-colors ${
                selectedToken === "USDC"
                  ? "border-[#00796F] bg-[#1B2030] text-[#00A69C]"
                  : "border-[#2B2F3B] hover:border-[#363A41] text-gray-300"
              }`}
            >
              USDC Lending
            </button>
            <button
              onClick={() => setSelectedToken("USDT")}
              className={`flex-1 py-3 rounded-xl border transition-colors ${
                selectedToken === "USDT"
                  ? "border-[#00796F] bg-[#1B2030] text-[#00A69C]"
                  : "border-[#2B2F3B] hover:border-[#363A41] text-gray-300"
              }`}
            >
              USDT Lending
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <div className="p-4 rounded-xl bg-[#2B2F3B]">
              <p className="text-sm text-gray-400 mb-1">
                Your Deposited Balance
              </p>
              <p className="text-lg font-medium text-white">
                {formattedValues.initialDeposit} {selectedToken}
              </p>
            </div>

            <div className="p-4 rounded-xl bg-[#2B2F3B]">
              <p className="text-sm text-gray-400 mb-1">
                Your Compounded Balance
              </p>
              <p className="text-lg font-medium text-white">
                {formattedValues.currentValue} {selectedToken}
              </p>
              <p className="text-sm text-teal-400">
                +{formattedValues.estimatedYield} {selectedToken} earned
              </p>
            </div>

            <div className="p-4 rounded-xl bg-[#2B2F3B]">
              <p className="text-sm text-gray-400 mb-1">
                Your {selectedToken} Balance
              </p>
              <p className="text-lg font-medium text-white">
                {tokenBalance
                  ? Number(
                      formatUnits(
                        tokenBalance.value,
                        config.contracts[selectedToken].decimals
                      )
                    ).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                  : "0.00"}
              </p>
            </div>

            <div className="p-4 rounded-xl bg-[#2B2F3B]">
              <p className="text-sm text-gray-400 mb-1">Current APY</p>
              <p className="text-lg font-medium text-white">{apy}%</p>
            </div>
          </div>

          {/* Action Selector */}
          <div className="flex gap-4 mb-6">
            <button
              onClick={() => setIsDeposit(true)}
              className={`flex-1 py-2 rounded-xl border transition-colors ${
                isDeposit
                  ? "border-[#00796F] bg-[#1B2030] text-[#00A69C]"
                  : "border-[#2B2F3B] hover:border-[#363A41] text-gray-300"
              }`}
            >
              Deposit
            </button>
            <button
              onClick={() => setIsDeposit(false)}
              className={`flex-1 py-2 rounded-xl border transition-colors ${
                !isDeposit
                  ? "border-[#00796F] bg-[#1B2030] text-[#00A69C]"
                  : "border-[#2B2F3B] hover:border-[#363A41] text-gray-300"
              }`}
            >
              Withdraw
            </button>
          </div>

          {/* Amount Input */}
          <div className="mb-6">
            <div className="flex items-center gap-2 sm:gap-4 p-3 sm:p-4 rounded-xl border border-[#2B2F3B] bg-[#1B2030]">
              <input
                type="number"
                value={amount}
                onChange={handleAmountInputChange}
                placeholder="0.0"
                className="flex-1 text-xl sm:text-2xl bg-transparent outline-none w-full min-w-0 text-white"
                style={{
                  WebkitAppearance: "none",
                  MozAppearance: "textfield",
                }}
              />
              <span className="text-gray-400 text-base sm:text-lg whitespace-nowrap">
                {selectedToken}
              </span>
            </div>
          </div>

          {/* Fee Calculation */}
          <div className="mb-6 p-4 rounded-xl bg-[#2B2F3B]">
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-gray-400">
                <span>Amount</span>
                <span>
                  {Number(amount || 0).toLocaleString()} {selectedToken}
                </span>
              </div>

              <div className="flex justify-between text-sm text-gray-400">
                <span>Service Fee ({serviceFee}%)</span>
                <span>
                  {serviceFee && amount
                    ? `${Number(
                        calculateFeeAmount
                      ).toLocaleString()} ${selectedToken}`
                    : `0.00 ${selectedToken}`}
                </span>
              </div>

              <div className="border-t border-[#363A41] pt-2 flex justify-between font-medium text-white">
                <span>You {isDeposit ? "Receive" : "Get"}</span>
                <span>
                  {serviceFee && amount
                    ? `${Number(finalAmount).toLocaleString()} ${selectedToken}`
                    : `0.00 ${selectedToken}`}
                </span>
              </div>
            </div>
          </div>

          {/* Projected Earnings */}
          <EarningsProjection
            amount={finalAmount}
            currentTotalDeposited={formattedValues.initialDeposit}
            apy={Number(apy)}
            selectedToken={selectedToken}
          />

          {/* Action Button */}
          <button
            onClick={handleAction}
            disabled={
              !isConnected ||
              isApproving ||
              isDepositing ||
              isWithdrawing ||
              !amount
            }
            className="w-full py-4 rounded-xl bg-[#00796F] text-white hover:bg-[#00A69C] transition-colors disabled:bg-[#2B2F3B] disabled:text-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isApproving || isDepositing || isWithdrawing ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : null}
            {getButtonText()}
          </button>
        </div>
      </div>

      {/* Footer */}
      <footer className="w-full p-4 border-t border-[#2B2F3B]">
        <div className="max-w-xl mx-auto text-center text-sm text-gray-400">
          <p>
            &copy; {new Date().getFullYear()} ezlend.xyz | EZLend Engineering |{" "}
            <a
              href={`mailto:referrals@ezlend.xyz?subject=Referral Program Application&body=Join our referral program to earn rewards! Email us your full name, number, social media accounts, and how you intend to refer our service`}
              className="text-[#00A69C] hover:text-[#00796F] transition-colors"
              onClick={() => {
                Mixpanel.track("Clicked Referral Email Link");
              }}
            >
              Referral Program
            </a>{" "}
            | All Rights Reserved
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LendingInterface;
